import { ReactNode, useCallback } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';

import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import styles from './DecisionInspectors.module.scss';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';

interface IDecisionInspectorsProps {
  watchOnly: boolean;
  name: string;
  titleMainPart: ReactNode;
  titleRestPart: ReactNode;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | IDictionaryParticipantPositionSelectOption[] | undefined;
  blockId: string;
  isDisableDelete?: boolean;
  getItemBlockId: (id: string) => string;
}

interface IDecisionInspectorsStoreProps {
  formRef: IDecisionFormRef | undefined;
}

const DecisionInspectorsWithoutStore = ({
  watchOnly,
  name,
  titleMainPart,
  titleRestPart,
  positionsSelectOptions,
  blockId,
  isDisableDelete,
  getItemBlockId,
  formRef,
}: IDecisionInspectorsProps & IDecisionInspectorsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addInspector = useCallback(() => {
    if (!watchOnly && push) {
      const addingInspector: IKnmErknmFormInspector = {
        fullName: undefined,
        position: undefined,
        guid: undefined,
        dateCreate: undefined,
        listId: uuidv4(),
      };

      push(name, addingInspector);
    }
  }, [name, push, watchOnly]);

  const makeRemoveInspectorHandler = (index: number) => () => !watchOnly && remove && remove(name, index);

  return (
    <FieldBlock
      // refactor it
      label={
        <>
          {titleMainPart}
          {titleRestPart}
          {getTitleFieldWithRedStar({
            label: ``,
            field: name,
            sectionName: name,
            isDecision: true,
          })}
        </>
      }
      id={blockId}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          id="erknmInspectorsAddButton"
          disabled={watchOnly}
          onClick={addInspector}
        />
      }
    >
      <FieldArray name={name}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormInspector, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((inspectorFieldName, sIndex) => {
                const fieldValue: IKnmErknmFormInspector = fields.value?.[sIndex];
                const id = getItemBlockId(fieldValue.listId);

                return (
                  <Row key={id} id={id} isListItem>
                    <FieldBlock size="half">
                      <TextInputField
                        className={styles.DecisionInspectorField}
                        name={`${inspectorFieldName}.fullName`}
                        placeholder={getTitleFieldWithRedStar({
                          label: `Введите ФИО должностного лица`,
                          field: `${inspectorFieldName}.fullName`,
                          isDecision: true,
                        })}
                        disabled={watchOnly}
                      />
                    </FieldBlock>
                    <FieldBlock size="half">
                      <div className="shared-field-row">
                        <SelectInputField
                          className={styles.DecisionInspectorField}
                          name={`${inspectorFieldName}.position`}
                          id={`${inspectorFieldName}.position`}
                          placeholder={getTitleFieldWithRedStar({
                            label: `Выберите должность`,
                            field: `${inspectorFieldName}.position`,
                            isDecision: true,
                          })}
                          options={positionsSelectOptions}
                          isSearchable
                          disabled={watchOnly}
                          size="large"
                        />
                        {(!fieldValue.guid || !isDisableDelete) && (
                          <CloseButton
                            className="shared-deleting-row-btn"
                            disabled={fieldValue.guid ? isDisableDelete : false}
                            onClick={makeRemoveInspectorHandler(sIndex)}
                          />
                        )}
                      </div>
                    </FieldBlock>
                  </Row>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IDecisionInspectorsStoreProps => ({
  formRef: getDecisionFormRef(state),
});

export const DecisionInspectors = connect(mapStateToProps)(DecisionInspectorsWithoutStore);
